import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Slider,
  Switch
} from '@mui/material';
import Lottie from 'lottie-react';

const AnimationSelectorDialog = ({ 
  open, 
  onClose, 
  product, 
  animationLibrary, 
  settings, 
  onSettingsChange,
  onSave 
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  const handleChange = (field, value) => {
    const newSettings = { ...localSettings, [field]: value };
    setLocalSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const selectedAnimation = animationLibrary.find(a => a.file === localSettings.file);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">Product Animation Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            {product?.name} - Double-click to configure animation
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* Enable Animation Toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
              }
              label="Enable Animation for this Product"
            />
          </Grid>

          {localSettings.enabled && (
            <>
              {/* Animation Library Grid */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Select Animation
                </Typography>
                <Grid container spacing={2}>
                  {animationLibrary.map((anim) => (
                    <Grid item xs={6} sm={4} md={3} key={anim.file}>
                      <Paper
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          border: localSettings.file === anim.file ? '2px solid #1976d2' : '1px solid #ddd',
                          bgcolor: localSettings.file === anim.file ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            bgcolor: '#f5f5f5'
                          }
                        }}
                        onClick={() => handleChange('file', anim.file)}
                      >
                        <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Lottie
                            animationData={anim.data}
                            loop={true}
                            autoplay={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </Box>
                        <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
                          {anim.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Position Selection */}
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Position</FormLabel>
                  <RadioGroup
                    value={localSettings.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                  >
                    <FormControlLabel value="top-left" control={<Radio />} label="Top Left Corner" />
                    <FormControlLabel value="top-right" control={<Radio />} label="Top Right Corner" />
                    <FormControlLabel value="bottom-left" control={<Radio />} label="Bottom Left Corner" />
                    <FormControlLabel value="bottom-right" control={<Radio />} label="Bottom Right Corner" />
                    <FormControlLabel value="center-overlay" control={<Radio />} label="Center Overlay" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Trigger Selection */}
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Animation Trigger</FormLabel>
                  <RadioGroup
                    value={localSettings.trigger}
                    onChange={(e) => handleChange('trigger', e.target.value)}
                  >
                    <FormControlLabel value="hover" control={<Radio />} label="On Hover/Tap" />
                    <FormControlLabel value="scroll" control={<Radio />} label="On Scroll Into View" />
                    <FormControlLabel value="always" control={<Radio />} label="Always Playing" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Size Slider */}
              <Grid item xs={12}>
                <FormLabel component="legend">Animation Size: {localSettings.size}px</FormLabel>
                <Slider
                  value={localSettings.size}
                  onChange={(e, value) => handleChange('size', value)}
                  min={40}
                  max={150}
                  step={10}
                  marks
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Preview */}
              {selectedAnimation && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f9fafb' }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: 200,
                        border: '2px dashed #ddd',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Product Card Mock */}
                      <Box
                        sx={{
                          width: 180,
                          height: 180,
                          bgcolor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: 2,
                          position: 'relative'
                        }}
                      >
                        <Typography variant="caption" sx={{ p: 1, display: 'block' }}>
                          Product Card
                        </Typography>
                        
                        {/* Animation Preview */}
                        <Box
                          sx={{
                            position: 'absolute',
                            ...(localSettings.position === 'top-left' && { top: 8, left: 8 }),
                            ...(localSettings.position === 'top-right' && { top: 8, right: 8 }),
                            ...(localSettings.position === 'bottom-left' && { bottom: 8, left: 8 }),
                            ...(localSettings.position === 'bottom-right' && { bottom: 8, right: 8 }),
                            ...(localSettings.position === 'center-overlay' && { 
                              top: '50%', 
                              left: '50%', 
                              transform: 'translate(-50%, -50%)' 
                            }),
                            width: localSettings.size,
                            height: localSettings.size,
                            zIndex: 10
                          }}
                        >
                          <Lottie
                            animationData={selectedAnimation.data}
                            loop={true}
                            autoplay={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(localSettings)}>
          Save Animation Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimationSelectorDialog;
